/**
 * WalletRegisterPanel.jsx — Inscription après connexion d'un wallet externe inconnu.
 *
 * État machine : WALLET_REGISTERING
 *
 * Contexte :
 *   L'utilisateur a connecté un wallet externe dont l'adresse n'est pas encore associée
 *   à un compte. POST /auth/verify-signature a retourné { action:'REGISTER', registrationToken }.
 *   Le wallet EST l'authentification → pas de mot de passe. On crée le compte via
 *   POST /auth/wallet/register en consommant le registrationToken (preuve SIWE court terme).
 *
 * Règles DA : §20 (zéro littéral hors theme.js) · §12 ter (zéro px en dur) · §18 ter (breakpoint)
 *   · §12 undecies (casse grammaticale) · §14 (zéro MUI) · §8.7 (zéro Redux).
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WalletBody,
  WalletFooter,
  FormGroup,
  FormLabel,
  FormInput,
  InlineError,
  PrimaryBtn,
  BackLink,
  AlertBox,
  AlertText,
  ToSText,
} from '../styles/EarniWalletModal.styles';
import { COLORS, SPACE, media } from '../../ui/theme';
import styled                   from 'styled-components';
import { ACTION_TYPES }         from '../hooks/useWalletStateMachine';
import { registerWithWallet }   from '../services/WalletAuthService';
import { useDisconnect }        from 'wagmi';
import { shortAddress as truncateAddress } from '../../../utils/format';

/* ─── Icône flèche retour ───────────────────────────────────────────────────── */
function IconArrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/* §18 ter — wrapper avec au moins 1 breakpoint responsive */
const RegisterPanelWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE.xs};
  ${media.sm`
    gap: 0;
  `}
`;

/* ─── Validation côté client ─────────────────────────────────────────────────── */
const EMAIL_RE    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-zA-Z0-9_-]{2,50}$/;

function validate({ username, email }) {
  if (!username)                   return 'Username is required.';
  if (!USERNAME_RE.test(username)) return 'Invalid username (2–50 characters, letters, numbers, _ or -).';
  if (email && !EMAIL_RE.test(email)) return 'Invalid email format.';
  return null;
}

/**
 * @param {object}   props
 * @param {function} props.dispatch           - dispatch de useWalletStateMachine
 * @param {string}   props.apiBaseUrl         - URL de base de l'API
 * @param {string}   props.connectedAddress   - adresse du wallet externe (lecture seule)
 * @param {string}   props.registrationToken  - preuve SIWE court terme (de verify-signature)
 */
export function WalletRegisterPanel({ dispatch, apiBaseUrl, connectedAddress, registrationToken }) {
  const { t } = useTranslation();
  const { disconnectAsync } = useDisconnect();

  const [username, setUsername] = useState('');
  const [email,    setEmail]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [formError, setFormError] = useState(null);

  const handleBack = useCallback(async () => {
    try { await disconnectAsync(); } catch { /* ignore */ }
    dispatch({ type: ACTION_TYPES.BACK });
  }, [dispatch, disconnectAsync]);

  const handleSubmit = useCallback(async () => {
    setFormError(null);
    const validationError = validate({ username, email });
    if (validationError) {
      setFormError(validationError);
      dispatch({ type: ACTION_TYPES.REGISTER_ERROR, payload: { message: validationError } });
      return;
    }
    if (!registrationToken) {
      const msg = 'Registration session expired. Reconnect your wallet.';
      setFormError(msg);
      dispatch({ type: ACTION_TYPES.REGISTER_ERROR, payload: { message: msg } });
      return;
    }
    setLoading(true);
    try {
      // Passer le code de parrainage s'il est présent dans l'URL (?ref=CODE)
      const refCode = new URLSearchParams(window.location.search).get('ref') || undefined;
      const user = await registerWithWallet(apiBaseUrl, {
        registrationToken,
        username,
        email: email || undefined,
        ...(refCode ? { ref: refCode } : {}),
      });
      dispatch({ type: ACTION_TYPES.REGISTER_SUCCESS, payload: user });
    } catch (err) {
      const msg = err?.message ?? 'Account creation failed. Please try again.';
      setFormError(msg);
      dispatch({ type: ACTION_TYPES.REGISTER_ERROR, payload: { message: msg } });
    } finally {
      setLoading(false);
    }
  }, [dispatch, apiBaseUrl, username, email, registrationToken]);

  const shortAddress = connectedAddress ? truncateAddress(connectedAddress) : '—';

  return (
    <>
      <WalletBody>
        <RegisterPanelWrap>
          <BackLink onClick={handleBack} type="button" aria-label={t('BackToSignInSelection')}>
            <IconArrow /> {t('BackAction')}
          </BackLink>

          {/* Adresse du wallet (lecture seule) */}
          <FormGroup>
            <FormLabel htmlFor="ewm-reg-address">{t('WalletAddressLabel')}</FormLabel>
            <FormInput
              id="ewm-reg-address"
              type="text"
              value={connectedAddress ?? ''}
              readOnly
              title={connectedAddress ?? ''}
              aria-label={t('ConnectedWalletAddress')}
              style={{ cursor: 'default', background: COLORS.bg }}
            />
            <span style={{ fontSize: '0.75rem', color: COLORS.muted }}>{shortAddress}</span>
          </FormGroup>

          <AlertBox $type="info" role="note">
            <AlertText $type="info" style={{ fontSize: '0.75rem' }}>
              {t('WalletSignInIdentifierNote')}
            </AlertText>
          </AlertBox>

          {/* Nom d'utilisateur (requis) */}
          <FormGroup>
            <FormLabel htmlFor="ewm-reg-username">{t('Username')} <span aria-hidden="true">*</span></FormLabel>
            <FormInput
              id="ewm-reg-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('UsernameNicknamePlaceholder')}
              autoComplete="username"
              disabled={loading}
              aria-required="true"
              $hasError={!!formError && !username}
            />
          </FormGroup>

          {/* Email (optionnel) */}
          <FormGroup>
            <FormLabel htmlFor="ewm-reg-email">{t('EmailAddress')} <span style={{ color: COLORS.muted }}>{t('OptionalParen')}</span></FormLabel>
            <FormInput
              id="ewm-reg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('EmailExamplePlaceholder')}
              autoComplete="email"
              disabled={loading}
            />
          </FormGroup>

          {formError && <InlineError role="alert">{formError}</InlineError>}
        </RegisterPanelWrap>
      </WalletBody>

      <WalletFooter>
        <PrimaryBtn type="button" onClick={handleSubmit} disabled={loading} aria-busy={loading}>
          {loading ? t('CreatingEllipsis') : t('CompleteRegistration')}
        </PrimaryBtn>

        <ToSText>
          By creating an account, you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of use</a>{' '}
          and our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy policy</a>.
        </ToSText>
      </WalletFooter>
    </>
  );
}

export default WalletRegisterPanel;
